// Minimal API client — wraps fetch with credentials + CSRF token.
const csrfTokenStorageKey = "tbcore_csrf";
const returnUrlStorageKey = "tbcore_return_url";
export function getCsrfToken() {
    return localStorage.getItem(csrfTokenStorageKey);
}
export function setCsrfToken(token) {
    localStorage.setItem(csrfTokenStorageKey, token);
}
export function clearCsrfToken() {
    localStorage.removeItem(csrfTokenStorageKey);
}
export function consumeReturnUrl() {
    const v = localStorage.getItem(returnUrlStorageKey);
    if (v)
        localStorage.removeItem(returnUrlStorageKey);
    return v;
}
/**
 * Persist where the user wanted to go before the 401 hit, so Login can
 * send them back there. Skipped on /login itself to avoid loop-pinning
 * the login page as the return target.
 */
function captureReturnUrl() {
    if (typeof window === "undefined")
        return;
    const path = window.location.pathname;
    if (path === "/login")
        return;
    const target = path + window.location.search;
    // Only set if we don't already have one queued — first 401 wins, so
    // chained failures (e.g. whoami + side-effect fetches in parallel)
    // don't overwrite the original landing target with /api/... paths.
    if (!localStorage.getItem(returnUrlStorageKey)) {
        localStorage.setItem(returnUrlStorageKey, target);
    }
}
/**
 * Hard navigate to /login when the session has expired. Using
 * window.location.assign avoids needing a router hook here in the
 * non-React API layer; replace state so the Back button doesn't drop
 * the user back onto the page that just 401'd.
 */
function redirectToLogin() {
    if (typeof window === "undefined")
        return;
    if (window.location.pathname === "/login")
        return;
    window.location.replace("/login");
}
async function request(path, init = {}, opts = {}) {
    const headers = {
        "Content-Type": "application/json",
        ...(init.headers || {}),
    };
    if (init.method && init.method !== "GET") {
        const csrf = getCsrfToken();
        if (csrf)
            headers["X-CSRF-Token"] = csrf;
    }
    const res = await fetch(path, {
        credentials: "include",
        ...init,
        headers,
    });
    if (!res.ok) {
        const text = await res.text();
        let detail = text;
        try {
            const j = JSON.parse(text);
            detail = j.detail ?? text;
        }
        catch {
            /* ignore */
        }
        if (res.status === 401 && !opts.skipAuthRedirect) {
            captureReturnUrl();
            redirectToLogin();
        }
        throw { status: res.status, detail };
    }
    return (await res.json());
}
export const api = {
    health: () => request("/api/health/"),
    healthDetailed: () => request("/api/health/detailed"),
    healthReady: () => request("/api/health/ready"),
    healthProbe: () => request("/api/health/probe"),
    healthPositionSync: () => request("/api/health/position-sync"),
    healthRunners: () => request("/api/health/runners"),
    healthDataFreshness: () => request("/api/health/data-freshness"),
    notificationChannels: () => request("/api/settings/notifications"),
    monitoringAlerts: (hours = 48) => request(`/api/monitoring/alerts?hours=${hours}`),
    healthRecentEvents: (limit = 25) => request(`/api/health/recent-events?limit=${limit}`),
    healthDecisionRates: () => request("/api/health/decision-rates"),
    healthBanner: () => request("/api/health/banner"),
    healthAckCreate: (check_name, detail_hash, reason, ttl_hours, scope = "team") => request("/api/health/ack", {
        method: "POST",
        body: JSON.stringify({ check_name, detail_hash, reason, ttl_hours, scope }),
    }),
    healthAckRevoke: (ack_id, reason) => request(`/api/health/ack/${ack_id}`, { method: "DELETE", body: JSON.stringify({ reason }) }),
    healthAcksList: (include_revoked = false) => request(`/api/health/acks?include_revoked=${include_revoked ? "true" : "false"}`),
    whoami: () => request("/api/auth/whoami", {}, { skipAuthRedirect: true }),
    login: (username, password) => request("/api/auth/login", {
        method: "POST",
        body: JSON.stringify({ username, password }),
    }),
    verifyTotp: (user, mfa_challenge, totp_code) => request("/api/auth/verify-totp", {
        method: "POST",
        body: JSON.stringify({ user, mfa_challenge, totp_code }),
    }),
    totpEnrollStart: () => request("/api/users/me/totp-enroll", { method: "POST" }),
    totpEnrollConfirm: (totp_code) => request("/api/users/me/totp-confirm", { method: "POST", body: JSON.stringify({ totp_code }) }),
    totpDisable: (totp_code) => request("/api/users/me/totp-disable", { method: "POST", body: JSON.stringify({ totp_code }) }),
    logout: () => request("/api/auth/logout", { method: "POST" }),
    labStrategies: () => request("/api/lab/strategies"),
    labFilters: () => request("/api/lab/filters"),
    labInstances: () => request("/api/lab/instances"),
    labCreateInstance: (body) => request("/api/lab/instances", {
        method: "POST",
        body: JSON.stringify(body),
    }),
    labPromoteInstance: (labId, body) => request(`/api/lab/instances/${encodeURIComponent(labId)}/promote`, {
        method: "POST",
        body: JSON.stringify(body),
    }),
    labDrift: (hours = 24) => request(`/api/lab/drift?hours=${hours}`),
    labDecisions: (limit = 200) => request(`/api/lab/decisions?limit=${limit}`),
    labDecisionsSummary: (hours = 24) => request(`/api/lab/decisions/summary?hours=${hours}`),
    labConversion: (hours = 24) => request(`/api/lab/conversion?hours=${hours}`),
    labStrategyDetails: () => request("/api/lab/strategies/details"),
    // #262: sinceRealismEpoch (default true) → lifetime KPIs only count trades
    // opened since the paper-realism cutoff; false → full history.
    labPaperMirrorKpis: (sinceRealismEpoch = true) => request(`/api/lab/paper-mirror-kpis?since_realism_epoch=${sinceRealismEpoch}`),
    labV2NativePaperKpis: (sinceRealismEpoch = true) => request(`/api/lab/v2-native-paper-kpis?since_realism_epoch=${sinceRealismEpoch}`),
    prodRealismCutoff: () => request("/api/prod/realism-cutoff"),
    edgeSuggestions: (status = "pending", instanceId, limit = 500) => {
        const qs = new URLSearchParams({ status, limit: String(limit) });
        if (instanceId)
            qs.set("instance_id", instanceId);
        return request(`/api/lab/edge-suggestions?${qs}`);
    },
    edgeSuggestionsCounts: () => request("/api/lab/edge-suggestions/counts"),
    edgeSuggestionApply: (id, body = {}) => request(`/api/lab/edge-suggestions/${id}/apply`, { method: "POST", body: JSON.stringify(body) }),
    edgeSuggestionCloneToPaper: (id) => request(`/api/lab/edge-suggestions/${id}/clone-to-paper`, { method: "POST" }),
    edgeSuggestionReject: (id, body = {}) => request(`/api/lab/edge-suggestions/${id}/reject`, { method: "POST", body: JSON.stringify(body) }),
    prodInstances: () => request("/api/prod/instances"),
    prodInstanceDetail: (refNo) => request(`/api/prod/instances/by-ref/${refNo}`),
    // #262: sinceRealismEpoch (default true) gates PAPER-instance lifetime KPIs
    // to the realism cutoff; live instances always show full history.
    prodInstanceKpis: (sinceRealismEpoch = true) => request(`/api/prod/instance-kpis?since_realism_epoch=${sinceRealismEpoch}`),
    prodUpdateInstance: (instanceId, body, opts = {}) => {
        const qs = opts.force ? "?force=true" : "";
        return request(`/api/prod/instances/${encodeURIComponent(instanceId)}${qs}`, { method: "PATCH", body: JSON.stringify(body) });
    },
    prodFreeSubAccounts: (mode) => request(`/api/prod/sub-accounts/free${mode ? `?mode=${mode}` : ""}`),
    prodReconcilerStatus: () => request("/api/prod/reconciler-status"),
    crossV1Live: (days = 30) => request(`/api/cross/v1-live?days=${days}`),
    crossMigrationPairs: (days = 14) => request(`/api/cross/migration-pairs?days=${days}`),
    goLiveGates: () => request("/api/health/go-live-gates"),
    prodTradeHistory: (params) => {
        const qs = new URLSearchParams();
        if (params?.days)
            qs.set("days", String(params.days));
        if (params?.instance_id)
            qs.set("instance_id", params.instance_id);
        if (params?.strategy_id)
            qs.set("strategy_id", params.strategy_id);
        if (params?.limit)
            qs.set("limit", String(params.limit));
        const q = qs.toString();
        return request(`/api/prod/trade-history${q ? `?${q}` : ""}`);
    },
    prodPositions: (instanceId, openOnly = true) => {
        const qs = new URLSearchParams();
        if (instanceId)
            qs.set("instance_id", instanceId);
        qs.set("open_only", String(openOnly));
        return request(`/api/prod/positions?${qs.toString()}`);
    },
    prodShadowKpi: (params) => {
        const qs = new URLSearchParams();
        if (params?.days)
            qs.set("days", String(params.days));
        if (params?.instance_id)
            qs.set("instance_id", params.instance_id);
        const q = qs.toString();
        return request(`/api/prod/shadow-kpi${q ? `?${q}` : ""}`);
    },
    prodShadowMismatches: (params) => {
        const qs = new URLSearchParams();
        if (params?.instance_id)
            qs.set("instance_id", params.instance_id);
        if (params?.hours)
            qs.set("hours", String(params.hours));
        if (params?.limit)
            qs.set("limit", String(params.limit));
        const q = qs.toString();
        return request(`/api/prod/shadow-mismatches${q ? `?${q}` : ""}`);
    },
    prodOrders: (params) => {
        const qs = new URLSearchParams();
        if (params?.position_id)
            qs.set("position_id", params.position_id);
        if (params?.instance_id)
            qs.set("instance_id", params.instance_id);
        if (params?.status)
            qs.set("status", params.status);
        if (params?.limit)
            qs.set("limit", String(params.limit));
        if (params?.include_paper)
            qs.set("include_paper", "true");
        const q = qs.toString();
        return request(`/api/prod/orders${q ? `?${q}` : ""}`);
    },
    prodCloseAll: (reason) => request("/api/prod/positions/close-all", { method: "POST", body: JSON.stringify({ reason }) }),
    prodBulkAllowedSides: (sides, scope = "active") => request("/api/prod/instances/bulk-allowed-sides", { method: "POST", body: JSON.stringify({ sides, scope }) }),
    prodPerformanceDaily: (days = 30) => request(`/api/prod/performance/daily?days=${days}`),
    prodPerformanceByInstance: (days = 30) => request(`/api/prod/performance/by-instance?days=${days}`),
    // V1-style period aggregates: { today: {...}, day_1: {...}, ..., all: {...} }
    prodPerformancePeriods: () => request("/api/prod/performance/periods"),
    prodPerformancePeriodsByInstance: () => request("/api/prod/performance/periods/by-instance"),
    prodDedicatedSaPools: () => request("/api/prod/dedicated-sa-pools"),
    prodHealthPaused: () => request("/api/prod/health-paused"),
    prodPortfolioSummary: (period = "30d", source = "all", bucketId = null) => request(`/api/prod/portfolio/summary?period=${period}&source=${source}` +
        (bucketId ? `&bucket_id=${encodeURIComponent(bucketId)}` : "")),
    prodPortfolioEquityCurve: (period = "30d", source = "all", bucketId = null) => request(`/api/prod/portfolio/equity-curve?period=${period}&source=${source}` +
        (bucketId ? `&bucket_id=${encodeURIComponent(bucketId)}` : "")),
    prodPortfolioByStrategy: (period = "30d", source = "all", bucketId = null) => request(`/api/prod/portfolio/by-strategy?period=${period}&source=${source}` +
        (bucketId ? `&bucket_id=${encodeURIComponent(bucketId)}` : "")),
    prodClosePosition: (positionId, body) => request(`/api/prod/positions/${encodeURIComponent(positionId)}/close`, { method: "POST", body: JSON.stringify(body) }),
    prodDecisions: (limit = 200) => request(`/api/prod/decisions?limit=${limit}`),
    prodDecisionsSummary: (hours = 24) => request(`/api/prod/decisions/summary?hours=${hours}`),
    prodConversion: (hours = 24) => request(`/api/prod/conversion?hours=${hours}`),
    prodBuckets: () => request("/api/prod/buckets"),
    prodBucketPools: () => request("/api/prod/bucket-pools"),
    prodWallets: () => request("/api/prod/wallets"),
    prodCreateSubAccount: (body) => request("/api/prod/sub-accounts", { method: "POST", body: JSON.stringify(body) }),
    prodUpdateSubAccount: (saId, body) => request(`/api/prod/sub-accounts/${encodeURIComponent(saId)}`, { method: "PATCH", body: JSON.stringify(body) }),
    prodSigningKeys: () => request("/api/prod/signing-keys"),
    prodCreateSigningKey: (body) => request("/api/prod/signing-keys", {
        method: "POST",
        body: JSON.stringify(body),
    }),
    prodArchiveSigningKey: (id) => request(`/api/prod/signing-keys/${encodeURIComponent(id)}/archive`, { method: "POST" }),
    prodBucketSubAccounts: (bucketId) => request(`/api/prod/buckets/${encodeURIComponent(bucketId)}/sub-accounts`),
    prodAddBucketSubAccount: (bucketId, body) => request(`/api/prod/buckets/${encodeURIComponent(bucketId)}/sub-accounts`, { method: "POST", body: JSON.stringify(body) }),
    prodRemoveBucketSubAccount: (bucketId, saId) => request(`/api/prod/buckets/${encodeURIComponent(bucketId)}/sub-accounts/${encodeURIComponent(saId)}`, { method: "DELETE" }),
    prodArchiveSubAccount: (saId) => request(`/api/prod/sub-accounts/${encodeURIComponent(saId)}/archive`, { method: "POST" }),
    prodCreateBucket: (body) => request("/api/prod/buckets", {
        method: "POST",
        body: JSON.stringify(body),
    }),
    prodUpdateBucket: (bucketId, body) => request(`/api/prod/buckets/${encodeURIComponent(bucketId)}`, { method: "PATCH", body: JSON.stringify(body) }),
    prodArchiveBucket: (bucketId) => request(`/api/prod/buckets/${encodeURIComponent(bucketId)}/archive`, { method: "POST" }),
    killSwitchState: () => request("/api/prod/kill-switch"),
    killSwitchActivate: () => request("/api/prod/kill-switch/activate", { method: "POST" }),
    killSwitchDeactivate: () => request("/api/prod/kill-switch/deactivate", { method: "POST" }),
    labBacktests: (limit = 50) => request(`/api/lab/backtests?limit=${limit}`),
    labHotScan: (limit = 30) => request(`/api/lab/hot-scan?limit=${limit}`),
    labRetrainingRuns: (strategyId, limit = 50) => {
        const qs = new URLSearchParams();
        if (strategyId)
            qs.set("strategy_id", strategyId);
        qs.set("limit", String(limit));
        return request(`/api/lab/retraining-runs?${qs.toString()}`);
    },
    usersList: () => request("/api/users/"),
    usersCreate: (body) => request("/api/users/", {
        method: "POST",
        body: JSON.stringify(body),
    }),
    usersUpdate: (username, body) => request(`/api/users/${encodeURIComponent(username)}`, { method: "PATCH", body: JSON.stringify(body) }),
    settingsRiskDefaults: () => request("/api/settings/risk-defaults"),
    settingsPatchRiskDefaults: (updates) => request("/api/settings/risk-defaults", {
        method: "PATCH",
        body: JSON.stringify(updates),
    }),
    // ----- Logs (#92): unified audit-event feed -----
    logs: (params) => {
        const qs = new URLSearchParams();
        if (params?.since)
            qs.set("since", params.since);
        if (params?.until)
            qs.set("until", params.until);
        if (params?.sources)
            qs.set("sources", params.sources);
        if (params?.level)
            qs.set("level", params.level);
        if (params?.instance_id)
            qs.set("instance_id", params.instance_id);
        if (params?.q)
            qs.set("q", params.q);
        if (params?.limit !== undefined)
            qs.set("limit", String(params.limit));
        if (params?.offset !== undefined)
            qs.set("offset", String(params.offset));
        const qstr = qs.toString();
        return request(`/api/logs/${qstr ? `?${qstr}` : ""}`);
    },
    logsUnreadCount: () => request("/api/logs/unread-count"),
    logsMarkSeen: () => request("/api/logs/mark-seen", {
        method: "POST",
    }),
    labWalkForward: (limit = 30) => request(`/api/lab/walk-forward?limit=${limit}`),
    labValidateSuite: (limit = 100, label) => request(`/api/lab/validate-suite?limit=${limit}${label ? `&label=${encodeURIComponent(label)}` : ""}`),
};
